import ChatIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import { Box, Button, CircularProgress, Divider, Fab, IconButton, Paper, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { type FormEvent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { useChatbot } from 'src/hooks/chat/useChatbot'
import { type FaqNode, faqRoot } from './faq'

/** 1セッションで LLM に投げられる回数。費用の歯止め */
const MAX_FREE_TEXT_ASKS = 5

type Message = {
  id: number
  from: 'bot' | 'user'
  text: string
  links?: { label: string; to: string }[]
}

export const ChatWidget = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { client } = useContext(ClientContext)
  const clientId = client?.id

  const [open, setOpen] = useState(false)
  const [nodes, setNodes] = useState<FaqNode[]>(faqRoot)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [askCount, setAskCount] = useState(0)
  const { ask, isAsking } = useChatbot()

  const seq = useRef(0)
  const nextId = useCallback(() => {
    seq.current += 1
    return seq.current
  }, [])

  const bottomRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isAsking])

  // モバイルでは全画面で開くため、背後のページがスクロールしないように止める。
  // PC では右下の小さいパネルなので、背後のページは普通にスクロールさせる。
  //
  // スクロールしているのは body ではなく html (documentElement) なので、
  // body だけに overflow: hidden を当てても止まらない。両方に当てる。
  useEffect(() => {
    if (!open || !isMobile) return

    const html = document.documentElement
    const body = document.body
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
    }

    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    // iOS Safari は overflow だけだと慣性スクロールが抜けることがある
    body.style.touchAction = 'none'

    return () => {
      html.style.overflow = prev.htmlOverflow
      body.style.overflow = prev.bodyOverflow
      body.style.touchAction = prev.bodyTouchAction
    }
  }, [open, isMobile])

  const pushBot = useCallback(
    (text: string, links?: { label: string; to: string }[]) => {
      setMessages((prev) => [...prev, { id: nextId(), from: 'bot', text, links }])
    },
    [nextId],
  )

  const handleOpen = useCallback(() => {
    setOpen(true)
    if (messages.length === 0) {
      pushBot('ワナップのご案内です。お困りのことを選んでください。下の入力欄から自由にご質問いただくこともできます。')
    }
  }, [messages.length, pushBot])

  const handleSelect = useCallback(
    (node: FaqNode) => {
      setMessages((prev) => [...prev, { id: nextId(), from: 'user', text: node.label }])

      if (node.children?.length) {
        setNodes(node.children)
        pushBot('どちらについてお知りになりたいですか？')
        return
      }

      if (node.answer) {
        pushBot(node.answer.text(clientId), node.answer.links?.(clientId))
        setNodes(faqRoot)
        pushBot('ほかにお困りのことはありますか？')
      }
    },
    [clientId, nextId, pushBot],
  )

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      const question = input.trim()
      if (!question || isAsking) return

      if (askCount >= MAX_FREE_TEXT_ASKS) {
        setMessages((prev) => [...prev, { id: nextId(), from: 'user', text: question }])
        setInput('')
        pushBot('ご質問が続いているようです。恐れ入りますが、お問い合わせから直接ご連絡ください。担当者が確認してご返信します。', inquiryLinks(clientId))
        return
      }

      setMessages((prev) => [...prev, { id: nextId(), from: 'user', text: question }])
      setInput('')
      setAskCount((c) => c + 1)

      // 直近のやり取りだけ渡す。全部渡すと入力トークンが増えるだけで精度は上がらない
      const history = messages
        .filter((m) => m.from === 'user' || m.from === 'bot')
        .slice(-6)
        .map((m) => ({ role: m.from === 'user' ? ('user' as const) : ('assistant' as const), content: m.text.slice(0, 400) }))

      const res = await ask({ question, history, isLoggedIn: !!clientId })
      pushBot(res.answer, res.unanswered ? inquiryLinks(clientId) : undefined)
      setNodes(faqRoot)
    },
    [ask, askCount, clientId, input, isAsking, messages, nextId, pushBot],
  )

  const handleReset = useCallback(() => {
    setNodes(faqRoot)
    pushBot('最初の選択肢に戻ります。お困りのことを選んでください。')
  }, [pushBot])

  if (!open) {
    return (
      <Fab
        color="primary"
        aria-label="ヘルプチャットを開く"
        onClick={handleOpen}
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <ChatIcon />
      </Fab>
    )
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        zIndex: (t) => t.zIndex.drawer + 1,
        display: 'flex',
        flexDirection: 'column',
        ...(isMobile ? { inset: 0, borderRadius: 0 } : { bottom: 24, right: 24, width: 380, height: 560, borderRadius: 2, maxHeight: 'calc(100vh - 48px)' }),
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
        >
          ヘルプ
        </Typography>
        <IconButton size="small" aria-label="ヘルプチャットを閉じる" onClick={() => setOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <Stack spacing={1.5}>
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} onNavigate={() => setOpen(false)} />
          ))}
          {isAsking && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <CircularProgress size={16} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                お調べしています…
              </Typography>
            </Stack>
          )}
          <div ref={bottomRef} />
        </Stack>
      </Box>

      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack spacing={1}>
          {nodes.map((node) => (
            <Button key={node.id} variant="outlined" size="small" onClick={() => handleSelect(node)} sx={{ justifyContent: 'flex-start' }}>
              {node.label}
            </Button>
          ))}
          {nodes !== faqRoot && (
            <Button size="small" onClick={handleReset} sx={{ justifyContent: 'flex-start' }}>
              最初に戻る
            </Button>
          )}
        </Stack>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="自由に質問する"
            id="chatInput"
            name="chatInput"
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            disabled={isAsking}
            slotProps={{
              htmlInput: { maxLength: 500 },
            }}
          />
          <IconButton type="submit" color="primary" aria-label="送信" disabled={isAsking || !input.trim()}>
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  )
}

const inquiryLinks = (clientId?: string) =>
  clientId ? [{ label: 'お問い合わせ', to: `/clients/${clientId}/inquiries` }] : [{ label: 'お問い合わせ', to: '/inquiry' }]

type MessageBubbleProps = {
  message: Message
  onNavigate: () => void
}

const MessageBubble = ({ message, onNavigate }: MessageBubbleProps) => {
  const isUser = message.from === 'user'

  return (
    <Stack
      sx={{
        alignItems: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: '85%',
          borderRadius: 2,
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
        }}
      >
        {message.text.split('\n').map((line, i) => (
          // 本文は静的な定型文か LLM の応答で、行の並びが入れ替わることはない
          // biome-ignore lint/suspicious/noArrayIndexKey: 行の並びが変わらない本文の分割表示
          <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {line}
          </Typography>
        ))}
        {message.links && message.links.length > 0 && (
          <Stack
            spacing={0.5}
            sx={{
              mt: 1,
            }}
          >
            {message.links.map((l) => (
              <Typography key={l.to} variant="body2">
                <Link to={l.to} onClick={onNavigate}>
                  {l.label}
                </Link>
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  )
}
